import * as React from "react";
import LocalSettings from "nexia_mobile/models/local_settings";
import * as DeviceFeatures from "nexia_mobile/models/mixins/device_features";
import {
  ActionButtons,
  ActivateAutomation,
  ActivateLockButton,
  Alarm,
  Barrier,
  Camera,
  Dimmer,
  Fan,
  LockAndUnlock,
  LockLockButton,
  MultiCamera,
  NestCamera,
  NestStructure,
  Speaker,
  Switch,
  Thermostat,
  Valve,
  WindowShadeOpenCloseStop,
  WindowShadeSetPercentOpen,
} from "nexia_mobile/components/Controls";

function createControlsForDevice(device, props = {}) {
  let controls = [];
  let index = 0;

  const pushControl = (componentType) => {
    const componentName = componentType.name || componentType.displayName;
    const key = `${componentName}-${index}`;
    const element = React.createElement(componentType, {
      model: device,
      key,
      ...props,
    });
    controls.push(element);
    index++;
  };

  if (
    LocalSettings.getMultiVideoEnabled() &&
    device.hasFeature(DeviceFeatures.Group) &&
    device.getGroupMembers().length > 0 &&
    device
      .getGroupMembers()
      .all((childDevice) => childDevice.hasFeature(DeviceFeatures.Camera))
  ) {
    pushControl(MultiCamera);
  }

  if (device.hasFeature(DeviceFeatures.Alarm) && device.canToggleAlarm()) {
    pushControl(Alarm);
  }

  if (device.hasFeature(DeviceFeatures.Barrier)) {
    pushControl(Barrier);
  }

  if (device.hasFeature(DeviceFeatures.Camera)) {
    pushControl(Camera);
  }

  if (device.hasFeature(DeviceFeatures.Dimmer)) {
    pushControl(Dimmer);
  }

  if (device.hasFeature(DeviceFeatures.Fan)) {
    pushControl(Fan);
  }

  if (
    device.hasFeature(DeviceFeatures.LockLock) &&
    device.hasFeature(DeviceFeatures.UnlockLock)
  ) {
    // Locks that can be remotely locked and unlocked

    if (!device.get("isLocked") && !device.get("isUnlocked")) {
      // Unknown state; show lock button
      pushControl(LockLockButton);
    } else {
      pushControl(LockAndUnlock);
    }
  } else if (
    device.hasFeature(DeviceFeatures.LockLock) &&
    device.hasFeature(DeviceFeatures.ActivateLock)
  ) {
    // Locks that can be remotely locked or activated, but not unlocked
    if (device.get("isActivatable")) {
      pushControl(ActivateLockButton);
    } else if (!device.get("isLocked")) {
      pushControl(LockLockButton);
    }
  } else if (device.hasFeature(DeviceFeatures.ActivateLock)) {
    // Locks that can be remotely activated, but not locked or unlocked
    pushControl(ActivateLockButton);
  }

  if (device.hasFeature(DeviceFeatures.Occupancy)) {
    pushControl(NestStructure);
  }

  if (device.hasFeature(DeviceFeatures.Streamable)) {
    pushControl(NestCamera);
  }

  if (device.hasFeature(DeviceFeatures.Speaker)) {
    pushControl(Speaker);
  }

  if (device.hasFeature(DeviceFeatures.Switch)) {
    pushControl(Switch);
  }

  if (
    device.hasFeature(DeviceFeatures.Thermostat) &&
    !device.hasFeature(DeviceFeatures.Group)
  ) {
    pushControl(Thermostat);
  }

  if (device.hasFeature(DeviceFeatures.Valve)) {
    pushControl(Valve);
  }

  if (device.hasFeature(DeviceFeatures.WindowShadeSetPercentOpen)) {
    pushControl(WindowShadeSetPercentOpen);
  }

  if (device.hasFeature(DeviceFeatures.WindowShadeOpenCloseStop)) {
    pushControl(WindowShadeOpenCloseStop);
  }

  if (device.hasFeature(DeviceFeatures.ActionButtons)) {
    pushControl(ActionButtons);
  }

  return controls;
}

function createControlsForBuilding(building, props = {}) {
  let controls = [];
  let index = 0;
  const devices = building.get("devices");

  const pushControl = (componentType, device) => {
    const componentName = componentType.name || componentType.displayName;
    const key = `${componentName}-${index}`;
    const element = React.createElement(componentType, {
      model: device,
      key,
      ...props,
    });
    controls.push(element);
    index++;
  };

  devices.each((device) => {
    if (device.hasFeature(DeviceFeatures.Alarm) && device.canToggleAlarm()) {
      pushControl(Alarm, device);
    }

    if (
      device.hasFeature(DeviceFeatures.Thermostat) &&
      !device.hasFeature(DeviceFeatures.Group)
    ) {
      pushControl(Thermostat, device);
    }
  });
  return controls;
}

export function createTileControlsForDevice(device, props = {}) {
  const controls = createControlsForDevice(device, {
    isDetailed: false,
    ...props,
  });
  return controls[0] || null;
}

export function createDetailedControlsForDevice(device, props = {}) {
  const controls = createControlsForDevice(device, {
    isDetailed: true,
    ...props,
  });
  if (controls.length === 0) {
    return null;
  }
  return controls;
}

export function createTileControlForAutomation(automation, props = {}) {
  return (
    <ActivateAutomation isDetailed={false} model={automation} {...props} />
  );
}

export function createDetailedControlForAutomation(automation, props = {}) {
  return <ActivateAutomation isDetailed model={automation} {...props} />;
}

export function createTileControlsForBuilding(building, props = {}) {
  const controls = createControlsForBuilding(building, {
    isDetailed: true,
    ...props,
  });
  return controls[0] || null;
}



// WEBPACK FOOTER //
// ./src/components/Controls/createControlsForModel.jsx